import { useState } from "react";
import { useLoaderData, useNavigate } from "react-router-dom";
import exportToCSV from "../components/exportToCSV";

const apiUrl = process.env.REACT_APP_API_BASE_URL;

export async function loader() {
  const token = localStorage.getItem("token");

  if (!token) {
    throw new Response(
      JSON.stringify({
        message: "You must be logged in to access this resource.",
      }),
      { status: 401 }
    );
  }

  const response = await fetch(`${apiUrl}/ct/affiliate`, {
    headers: {
      Authorization: `Bearer ${token}`,
      "X-App-Secret": process.env.REACT_APP_APP_SECRET,
    },
  });

  if (!response.ok) {
    throw new Response(
      JSON.stringify({ message: "Could not fetch affiliate codes." }),
      { status: response.status }
    );
  }

  const affiliateCodes = await response.json();
  return affiliateCodes;
}

export default function AffiliateCodesManagement() {
  const affiliateCodes = useLoaderData();
  const navigate = useNavigate();

  const [filters, setFilters] = useState({
    affiliate_id: "",
    id: "",
  });
  const [editRowId, setEditRowId] = useState(null);
  const [editableData, setEditableData] = useState({});
  const [newAffiliateId, setNewAffiliateId] = useState("");

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters((prev) => ({ ...prev, [name]: value }));
  };

  // Filter records based on the affiliate_id and id filters.
  console.log(affiliateCodes);
  const filteredData = affiliateCodes.filter((item) =>
    Object.entries(filters).every(([key, value]) => {
      if (!value) return true;
      return String(item[key]).toLowerCase().includes(value.toLowerCase());
    })
  );

  const handleEditClick = (id, rowData) => {
    setEditRowId(id);
    setEditableData({
      affiliate_id: rowData.affiliate_id,
    });
  };

  const handleSaveClick = async (id) => {
    try {
      const token = localStorage.getItem("token");

      if (!token) {
        alert("You must be logged in to save changes.");
        navigate("/login");
        return;
      }

      const response = await fetch(`${apiUrl}/ct/affiliate/${id}`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
          "X-App-Secret": process.env.REACT_APP_APP_SECRET,
        },
        body: JSON.stringify({
          affiliate_id: editableData.affiliate_id,
        }),
      });

      if (!response.ok) {
        throw new Error("Failed to update the affiliate code");
      }

      const updatedItem = await response.json();
      alert(updatedItem.message || "Affiliate code updated successfully.");

      // Optionally refresh the data after update.
      setEditRowId(null);
      navigate(".", { replace: true });
    } catch (err) {
      console.error("Error updating affiliate code:", err);
      alert("An error occurred while saving the affiliate code.");
    }
  };

  const handleEditChange = (e) => {
    const { name, value } = e.target;
    setEditableData((prev) => ({ ...prev, [name]: value }));
  };

  const handleDeleteClick = async (id) => {
    if (!window.confirm("Are you sure you want to delete this affiliate code?"))
      return;
    try {
      const token = localStorage.getItem("token");
      if (!token) {
        alert("You must be logged in to delete affiliate codes.");
        navigate("/login");
        return;
      }

      const response = await fetch(`${apiUrl}/ct/affiliate/${id}`, {
        method: "DELETE",
        headers: {
          Authorization: `Bearer ${token}`,
          "X-App-Secret": process.env.REACT_APP_APP_SECRET,
        },
      });

      if (!response.ok) {
        throw new Error("Failed to delete the affiliate code");
      }

      const result = await response.json();
      alert(result.message || "Affiliate code deleted successfully.");
      navigate(".", { replace: true });
    } catch (err) {
      console.error("Error deleting affiliate code:", err);
      alert("An error occurred while deleting the affiliate code.");
    }
  };

  const handleCreateClick = async () => {
    try {
      const token = localStorage.getItem("token");
      if (!token) {
        alert("You must be logged in to create an affiliate code.");
        navigate("/login");
        return;
      }

      if (!newAffiliateId) {
        alert("Please enter an affiliate ID.");
        return;
      }

      const response = await fetch(`${apiUrl}/ct/affiliate/${newAffiliateId}`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
          "X-App-Secret": process.env.REACT_APP_APP_SECRET,
        },
        // The POST route uses the URL parameter as the new affiliate code.
        // Optionally, include additional data in the body if needed.
        body: JSON.stringify({ affiliate_id: newAffiliateId }),
      });

      if (!response.ok) {
        throw new Error("Failed to create the affiliate code");
      }

      const result = await response.json();
      alert(result.message || "Affiliate code created successfully.");
      setNewAffiliateId("");
      navigate(".", { replace: true });
    } catch (err) {
      console.error("Error creating affiliate code:", err);
      alert("An error occurred while creating the affiliate code.");
    }
  };

  return (
    <div className="affiliate-codes-container">
      <h1>Affiliate Codes Management</h1>

      {/* Section for creating a new affiliate code */}
      <div className="create-affiliate-code">
        <h2>Create New Affiliate Code</h2>
        <label>
          Affiliate ID:
          <input
            type="text"
            name="newAffiliateId"
            value={newAffiliateId}
            onChange={(e) => setNewAffiliateId(e.target.value)}
          />
        </label>
        <button className="create-btn" onClick={handleCreateClick}>
          Add Affiliate Code
        </button>
      </div>

      {/* Filters */}
      <div className="filters">
        <label>
          Affiliate ID:
          <input
            type="text"
            name="affiliate_id"
            value={filters.affiliate_id}
            onChange={handleFilterChange}
          />
        </label>
        <label>
          ID:
          <input
            type="text"
            name="id"
            value={filters.id}
            onChange={handleFilterChange}
          />
        </label>
      </div>

      {/* CSV Export */}
      <div className="export-button">
        <button
          onClick={() => exportToCSV(filteredData, "affiliate_codes.csv")}
          className="export-btn"
        >
          Export CSV
        </button>
      </div>

      {/* Table of affiliate codes */}
      <table className="management-table">
        <thead>
          <tr>
            <th>ID</th>
            <th>Affiliate ID</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          {filteredData.map((item) => (
            <tr key={item.id}>
              {editRowId === item.id ? (
                <>
                  <td>{item.id}</td>
                  <td>
                    <input
                      type="text"
                      name="affiliate_id"
                      value={editableData.affiliate_id}
                      onChange={handleEditChange}
                    />
                  </td>
                  <td>
                    <button
                      className="save-btn"
                      onClick={() => handleSaveClick(item.id)}
                    >
                      Save
                    </button>
                  </td>
                </>
              ) : (
                <>
                  <td>{item.id}</td>
                  <td>{item.affiliate_id}</td>
                  <td>
                    <button
                      className="edit-btn"
                      onClick={() => handleEditClick(item.id, item)}
                    >
                      Edit
                    </button>
                    <button
                      className="delete-btn"
                      onClick={() => handleDeleteClick(item.id)}
                    >
                      Delete
                    </button>
                  </td>
                </>
              )}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
